import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import type { InferProcedureOutput } from "@goose/api";
import type { AvailableModelName } from "@goose/llm/models";
import type { TokenUsage } from "@goose/llm/tokens";
import type { ThinkingLevel } from "@goose/schema/ThinkingLevel";

import { CandidateStep } from "~/components/CandidateStep";
import { JobStep } from "~/components/JobStep";
import { ResultsStep } from "~/components/ResultsStep";
import { ThinkingAnimation } from "~/components/ThinkingAnimation";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { type Step, ProgressSteps } from "~/components/ui/ProgressSteps";

import type {
  EvaluateFitEvent,
  ExtractRequirementsEvent,
  ModifyRequirementsEvent,
} from "~/lib/events";

import { MODIFICATIONS_MODEL } from "~/lib/models";
import { useTRPC } from "~/lib/trpc";

const PARSE_LOADING_MESSAGES = [
  "Extracting requirements from recruiter prose…",
  "Counting verbs that imply ownership…",
  "Decoding acronyms…",
  "Reticulating splines for the skill graph…",
  "Summarizing responsibilities into human-friendly bullets…",
  "Disambiguating ‘preferred’ vs ‘required’…",
  "Measuring years of experience…",
  "Indexing stack buzzwords and nice-to-haves…",
  "Finding location and work-style fine print…",
];

const EVALUATE_LOADING_MESSAGES = [
  "Fitting skills to requirements…",
  "Scoring strength of evidence…",
  "Normalizing job titles…",
  "Reticulating splines for the match matrix…",
  "Detecting culture add (not just fit)…",
  "Aligning certs with the cloud stack…",
  "Weighing recency versus depth…",
  "Cross-referencing location and work style…",
  "Estimating ramp speed and impact…",
];

type StepKey = "job" | "candidate" | "results";

const STEPS: Step<StepKey>[] = [
  { key: "job", label: "Job Description" },
  { key: "candidate", label: "Candidate" },
  { key: "results", label: "Results" },
] as const;

interface JobStepState {
  step: "job";
}

interface CandidateStepState {
  step: "candidate";
  model: AvailableModelName;
  thinkingLevel: ThinkingLevel;
  jobText: string;
  extract: InferProcedureOutput<"requirements.extract">;
  modifications: InferProcedureOutput<"requirements.modify">[];
  refinementUsage: TokenUsage;
}

interface ResultsStepState extends Omit<CandidateStepState, "step"> {
  step: "results";
  candidateText: string;
  evaluation: InferProcedureOutput<"fit.evaluate">;
}

type State = JobStepState | CandidateStepState | ResultsStepState;

export const Route = createFileRoute("/_authenticated/admin/demo")({
  component: DemoPage,
});

function DemoPage() {
  const [state, setState] = useState<State>({ step: "job" });

  // TanStack Query hooks
  const trpc = useTRPC();

  const extractRequirementsMutation = useMutation(
    trpc.requirements.extract.mutationOptions(),
  );

  const modifyRequirementsMutation = useMutation(
    trpc.requirements.modify.mutationOptions(),
  );

  const evaluateFitMutation = useMutation(trpc.fit.evaluate.mutationOptions());

  // Extract loading and error states from mutations
  const loading =
    extractRequirementsMutation.isPending ||
    modifyRequirementsMutation.isPending ||
    evaluateFitMutation.isPending;

  const error =
    extractRequirementsMutation.error ??
    modifyRequirementsMutation.error ??
    evaluateFitMutation.error;

  const extractRequirements = async ({
    model,
    jobText,
    thinkingLevel,
  }: ExtractRequirementsEvent) => {
    const extract = await extractRequirementsMutation.mutateAsync({
      model,
      jobText,
      thinkingLevel,
    });

    setState({
      step: "candidate",
      model,
      thinkingLevel,
      jobText,
      extract,
      modifications: [],
      refinementUsage: { inputTokenCount: 0, outputTokenCount: 0 },
    });
  };

  const modifyRequirements = async ({
    modification,
  }: ModifyRequirementsEvent) => {
    if (state.step === "job") {
      throw new Error("No requirements to modify");
    }

    const { requirements: currentRequirements } =
      state.modifications[state.modifications.length - 1] ?? state.extract;

    const modifications = await modifyRequirementsMutation.mutateAsync({
      model: MODIFICATIONS_MODEL,
      jobText: state.jobText,
      currentRequirements,
      modification,
    });

    setState({
      ...state,
      modifications: [...state.modifications, modifications],
    });

    return modifications.requirements;
  };

  const evaluateFit = async ({ candidateText }: EvaluateFitEvent) => {
    if (state.step === "job") {
      throw new Error("No requirements to evaluate");
    }

    const { requirements } =
      state.modifications[state.modifications.length - 1] ?? state.extract;

    const evaluation = await evaluateFitMutation.mutateAsync({
      model: state.model,
      thinkingLevel: state.thinkingLevel,
      requirements,
      candidateText,
    });

    setState({
      step: "results",
      model: state.model,
      thinkingLevel: state.thinkingLevel,
      jobText: state.jobText,
      extract: state.extract,
      modifications: state.modifications,
      refinementUsage: state.refinementUsage,
      candidateText,
      evaluation,
    });
  };

  const reset = () => {
    setState({ step: "job" });
    extractRequirementsMutation.reset();
    modifyRequirementsMutation.reset();
    evaluateFitMutation.reset();
  };

  const onRefinementUsage = useCallback(
    (usage: TokenUsage) => {
      setState((current) => ({
        ...current,
        refinementUsage: usage,
      }));
    },
    [setState],
  );

  return (
    <div className="mt-6 sspace-y-6">
      {/* Thinking Animation Overlay */}
      {loading && (
        <ThinkingAnimation
          message={
            extractRequirementsMutation.isPending
              ? "Parsing Job Description"
              : "Evaluating Candidate"
          }
          submessage={
            extractRequirementsMutation.isPending
              ? PARSE_LOADING_MESSAGES
              : EVALUATE_LOADING_MESSAGES
          }
        />
      )}

      <div className="mx-auto max-w-6xl px-1 pb-2">
        <ProgressSteps active={state.step} steps={STEPS} />
        {error && <TRPCClientErrorDisplay error={error} />}

        {/* Step 1: Job Description */}
        {state.step === "job" && (
          <JobStep
            loading={loading}
            onExtract={(data) => void extractRequirements(data)}
          />
        )}

        {/* Step 2: Candidate Evaluation */}
        {state.step === "candidate" && (
          <CandidateStep
            model={state.model}
            jobText={state.jobText}
            extract={state.extract}
            modifications={state.modifications}
            refinementUsage={state.refinementUsage}
            loading={loading}
            onEvaluate={(data) => void evaluateFit(data)}
            onModify={modifyRequirements}
            onRefinementUsage={onRefinementUsage}
            onBack={reset}
          />
        )}

        {/* Step 3: Results */}
        {state.step === "results" && (
          <ResultsStep
            model={state.model}
            extract={state.extract}
            modifications={state.modifications}
            refinementUsage={state.refinementUsage}
            evaluation={state.evaluation}
            onReset={reset}
            onGoBack={() => {
              evaluateFitMutation.reset();
              setState({
                step: "candidate",
                model: state.model,
                thinkingLevel: state.thinkingLevel,
                jobText: state.jobText,
                extract: state.extract,
                modifications: state.modifications,
                refinementUsage: state.refinementUsage,
              });
            }}
          />
        )}
      </div>
    </div>
  );
}
